import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { Inbox as InboxIcon, Play, VideoOff } from "lucide-react";
import toast from "react-hot-toast";
import {
  useInboxCaptures,
  useAssignCapture,
  useDeleteCapture,
} from "@/hooks/useInbox";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import VideoPlayerModal from "@/components/video/VideoPlayerModal";
import { formatWindow } from "@/utils/timeFormat";
import { apiClient } from "@/services/api";
import type { InboxCapture, Session, VideoFile } from "@/types";

function captureWindow(capture: InboxCapture): [string | null, string | null] {
  // Truthy, not `!== null`: a backend predating the recorded fields leaves
  // them undefined over the wire — fall back to the stream window then too.
  return capture.recordedStartAt
    ? [capture.recordedStartAt, capture.recordedEndAt]
    : [capture.firstStartedAt, capture.lastEndedAt];
}

function CaptureThumbnail({
  capture,
  disabled,
  onWatch,
}: {
  capture: InboxCapture;
  disabled: boolean;
  onWatch: () => void;
}) {
  const { t } = useTranslation(["pages"]);
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const frameClass =
    "relative aspect-video w-28 shrink-0 overflow-hidden rounded-md bg-gray-100 dark:bg-gray-800 sm:w-36";

  if (capture.videoCount === 0) {
    return (
      <div
        className={`${frameClass} flex flex-col items-center justify-center gap-1 text-gray-500 dark:text-gray-400`}
      >
        <VideoOff className="h-6 w-6" aria-hidden="true" />
        <span className="text-xs">{t("pages:inbox.noRecording")}</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`${frameClass} group flex items-center justify-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50`}
      aria-label={t("pages:inbox.watchCapture", {
        time: formatWindow(...captureWindow(capture)),
      })}
      disabled={disabled}
      onClick={onWatch}
    >
      {capture.thumbnailUrl && capture.thumbnailUrl !== failedUrl && (
        <img
          src={capture.thumbnailUrl}
          alt=""
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover"
          onError={() => setFailedUrl(capture.thumbnailUrl ?? null)}
        />
      )}
      <span className="relative flex h-8 w-8 items-center justify-center rounded-full border border-white/70 bg-black/60 text-white group-hover:bg-black/80">
        <Play
          className="ml-0.5 h-4 w-4"
          fill="currentColor"
          aria-hidden="true"
        />
      </span>
      {capture.videoCount > 1 && (
        <span className="absolute bottom-1 left-1 rounded bg-black/75 px-1.5 py-0.5 text-[10px] leading-tight text-white sm:text-xs">
          {t("pages:inbox.videoCount", { count: capture.videoCount })}
        </span>
      )}
    </button>
  );
}

function AssignDialog({
  capture,
  onClose,
}: {
  capture: InboxCapture;
  onClose: () => void;
}) {
  const { t } = useTranslation(["pages", "common"]);
  const [search, setSearch] = useState("");
  const [selectedSession, setSelectedSession] = useState<Session | null>(null);
  const [sessions, setSessions] = useState<Session[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const assignMutation = useAssignCapture();

  useEffect(() => {
    apiClient
      .getSessions({
        scope: "mine",
        limit: 50,
        orderBy: "conductedAt",
        orderDirection: "desc",
      })
      .then(setSessions)
      .catch(() => setLoadFailed(true));
  }, []);

  const filtered = (sessions ?? []).filter((s) =>
    s.title.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-label={t("pages:inbox.assignTitle")}
    >
      <div className="w-full max-w-md rounded-lg bg-white p-4 shadow-lg dark:bg-gray-800">
        <h2 className="mb-3 text-lg font-semibold">
          {t("pages:inbox.assignTitle")}
        </h2>
        <input
          className="mb-2 w-full rounded border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-700"
          placeholder={t("pages:inbox.assignSearchPlaceholder")}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <ul className="max-h-64 space-y-1 overflow-y-auto">
          {loadFailed && (
            <li className="p-2 text-sm text-red-500">
              {t("common:errors.generic")}
            </li>
          )}
          {!loadFailed && filtered.length === 0 && (
            <li className="p-2 text-sm text-gray-500">
              {t("pages:inbox.noSessions")}
            </li>
          )}
          {filtered.map((session) => (
            <li key={session.id}>
              <button
                type="button"
                className={`w-full rounded px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 ${
                  selectedSession?.id === session.id
                    ? "bg-blue-50 dark:bg-blue-900/40"
                    : ""
                }`}
                onClick={() => setSelectedSession(session)}
              >
                {session.title}
              </button>
            </li>
          ))}
        </ul>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            className="rounded border border-gray-300 px-3 py-1.5 text-sm"
            onClick={onClose}
          >
            {t("pages:inbox.assignCancel")}
          </button>
          <button
            type="button"
            className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white disabled:opacity-50"
            disabled={!selectedSession || assignMutation.isPending}
            onClick={() => {
              if (!selectedSession) return;
              assignMutation.mutate(
                { captureId: capture.captureId, sessionId: selectedSession.id },
                { onSuccess: onClose },
              );
            }}
          >
            {t("pages:inbox.assignConfirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function InboxPage() {
  const { t } = useTranslation(["pages"]);
  const queryClient = useQueryClient();
  const { data: captures, isLoading, isError } = useInboxCaptures();
  const [assigning, setAssigning] = useState<InboxCapture | null>(null);
  const deleteMutation = useDeleteCapture();
  const [deleting, setDeleting] = useState<InboxCapture | null>(null);
  const [watchPlaylist, setWatchPlaylist] = useState<{
    captureId: string;
    videos: VideoFile[];
  } | null>(null);
  const [loadingWatchId, setLoadingWatchId] = useState<string | null>(null);

  async function handleWatch(capture: InboxCapture) {
    setLoadingWatchId(capture.captureId);
    try {
      const videos = await apiClient.getInboxCaptureVideos(capture.captureId);
      if (videos.length > 0) {
        setWatchPlaylist({ captureId: capture.captureId, videos });
      }
    } catch {
      toast.error(t("pages:inbox.watchUnavailable"));
      queryClient.invalidateQueries({ queryKey: ["trackcast", "inbox"] });
    } finally {
      setLoadingWatchId(null);
    }
  }

  return (
    <div className="mx-auto max-w-5xl p-4" data-testid="inbox-page">
      <div className="mb-4 flex items-center gap-2">
        <InboxIcon className="h-6 w-6" />
        <h1 className="text-2xl font-bold">{t("pages:inbox.title")}</h1>
      </div>
      <p className="mb-6 text-sm text-gray-500">{t("pages:inbox.subtitle")}</p>

      {isLoading && (
        <div className="py-10 text-center text-sm text-gray-500">…</div>
      )}

      {isError && (
        <div className="py-10 text-center text-sm text-red-500">
          {t("common:errors.generic")}
        </div>
      )}

      {!isLoading && !isError && (captures ?? []).length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 p-10 text-center dark:border-gray-600">
          <InboxIcon className="mx-auto mb-2 h-8 w-8 text-gray-400" />
          <p className="font-medium">{t("pages:inbox.empty")}</p>
          <p className="mt-1 text-sm text-gray-500">
            {t("pages:inbox.emptyHint")}
          </p>
        </div>
      )}

      <ul className="space-y-3">
        {(captures ?? []).map((capture) => (
          <li
            key={capture.captureId}
            className="flex flex-wrap items-center gap-3 rounded-lg border border-secondary-200 bg-white p-3 sm:gap-4 sm:p-4"
          >
            <CaptureThumbnail
              capture={capture}
              disabled={loadingWatchId !== null}
              onWatch={() => handleWatch(capture)}
            />
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                {capture.live && (
                  <span className="rounded bg-red-600 px-1.5 py-0.5 text-xs font-semibold text-white">
                    {t("pages:inbox.live")}
                  </span>
                )}
                <span className="text-sm tabular-nums">
                  {formatWindow(...captureWindow(capture))}
                </span>
              </div>
              <div className="mt-1 text-sm text-gray-500">
                <span>
                  {capture.attemptCount === 1
                    ? t("pages:inbox.attemptsOne")
                    : t("pages:inbox.attempts", {
                        count: capture.attemptCount,
                      })}
                </span>
                {" · "}
                <span>
                  {capture.videoCount === 1
                    ? t("pages:inbox.videosOne")
                    : t("pages:inbox.videos", { count: capture.videoCount })}
                </span>
              </div>
            </div>
            <div className="flex w-full shrink-0 flex-wrap justify-end gap-2 lg:w-auto">
              {capture.videoCount > 0 && (
                <button
                  type="button"
                  className="rounded border border-blue-600 px-3 py-1.5 text-sm text-blue-600 disabled:opacity-50 dark:text-blue-400"
                  disabled={loadingWatchId !== null}
                  onClick={() => handleWatch(capture)}
                >
                  {loadingWatchId === capture.captureId
                    ? "…"
                    : t("pages:inbox.watch")}
                </button>
              )}
              <button
                type="button"
                className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white"
                onClick={() => setAssigning(capture)}
              >
                {t("pages:inbox.assign")}
              </button>
              <button
                type="button"
                className="rounded border border-red-600 px-3 py-1.5 text-sm text-red-600 disabled:opacity-50 dark:text-red-400"
                disabled={capture.live}
                onClick={() => setDeleting(capture)}
              >
                {t("pages:inbox.delete")}
              </button>
            </div>
          </li>
        ))}
      </ul>

      {assigning && (
        <AssignDialog capture={assigning} onClose={() => setAssigning(null)} />
      )}
      <ConfirmationModal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (!deleting) return;
          deleteMutation.mutate(
            { captureId: deleting.captureId },
            { onSuccess: () => setDeleting(null) },
          );
        }}
        title={t("pages:inbox.deleteTitle")}
        message={t("pages:inbox.deleteMessage")}
        confirmButtonText={t("pages:inbox.deleteConfirm")}
        confirmButtonColor="red"
        isLoading={deleteMutation.isPending}
      />
      <VideoPlayerModal
        isOpen={watchPlaylist !== null}
        onClose={() => setWatchPlaylist(null)}
        playlist={watchPlaylist?.videos ?? []}
        onSegmentError={() =>
          toast.error(t("pages:inbox.watchUnavailable"), {
            id: "inbox-segment-unavailable",
          })
        }
      />
    </div>
  );
}
